import React, { useEffect, useRef, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_HIDDEN = { opacity: 0, y: 20 };
const HERO_VISIBLE = { opacity: 1, y: 0 };
const HERO_TRANSITION = { duration: 1.2, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] };
const SCROLL_HIDDEN = { opacity: 0 };
const SCROLL_VISIBLE = { opacity: 1 };
const SCROLL_TRANSITION = { duration: 1, delay: 1.5 };

/* ── Animated Connection Network Canvas ── */
const NetworkCanvas = () => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth * 2;
      canvas.height = window.innerHeight * 2;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
    };
    resize();
    window.addEventListener('resize', resize);

    // Create nodes
    const nodeCount = 60;
    if (nodesRef.current.length === 0) {
      for (let i = 0; i < nodeCount; i++) {
        nodesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.6,
          vy: (Math.random() - 0.5) * 0.6,
          r: Math.random() * 2 + 1,
          brightness: Math.random() * 0.5 + 0.3,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    }

    const connectionDist = canvas.width * 0.18;

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;

      // Dark gradient background
      const bg = ctx.createRadialGradient(w * 0.5, h * 0.45, 0, w * 0.5, h * 0.45, w * 0.7);
      bg.addColorStop(0, '#141828');
      bg.addColorStop(0.5, '#0e1220');
      bg.addColorStop(1, '#080c16');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      const nodes = nodesRef.current;

      // Update positions
      for (const node of nodes) {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.015;

        if (node.x < 0 || node.x > w) node.vx *= -1;
        if (node.y < 0 || node.y > h) node.vy *= -1;
        node.x = Math.max(0, Math.min(w, node.x));
        node.y = Math.max(0, Math.min(h, node.y));
      }

      // Draw connections
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDist) {
            const opacity = (1 - dist / connectionDist) * 0.25;
            const gold = Math.sin(nodes[i].pulse) * 0.5 + 0.5;

            const r = Math.round(160 + gold * 55);
            const g = Math.round(150 + gold * 40);
            const b = Math.round(100 + (1 - gold) * 80);

            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(${r},${g},${b},${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const pulse = Math.sin(node.pulse) * 0.3 + 0.7;
        const alpha = node.brightness * pulse;

        // Outer glow
        const glow = ctx.createRadialGradient(node.x, node.y, 0, node.x, node.y, node.r * 12);
        glow.addColorStop(0, `rgba(212,185,80,${alpha * 0.15})`);
        glow.addColorStop(1, 'rgba(212,185,80,0)');
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r * 12, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Core
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.r * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(230,215,150,${alpha * 0.8})`;
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: 'block' }}
    />
  );
};

/* ── Home Page ── */
const OnePageHome = () => {
  const [heroLoaded, setHeroLoaded] = React.useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setHeroLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const heroAnimate = useMemo(
    () => (heroLoaded ? HERO_VISIBLE : HERO_HIDDEN),
    [heroLoaded]
  );
  const scrollAnimate = useMemo(
    () => (heroLoaded ? SCROLL_VISIBLE : SCROLL_HIDDEN),
    [heroLoaded]
  );

  return (
    <div className="min-h-screen bg-white">
      <section
        id="hero"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Animated network background */}
        <div className="absolute inset-0">
          <NetworkCanvas />
        </div>

        <div className="relative z-10 text-center px-6 md:px-12 lg:px-24 max-w-5xl mx-auto">
          <motion.div
            initial={HERO_HIDDEN}
            animate={heroAnimate}
            transition={HERO_TRANSITION}
          >
            <div className="mb-8">
              <div className="w-16 h-px bg-[#D4AF37] mx-auto mb-8 opacity-60"></div>
              <p
                data-testid="hero-overline"
                className="text-[11px] font-medium uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-6"
              >
                Est. 2017
              </p>
            </div>

            <h1
              data-testid="hero-heading"
              className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light text-white uppercase leading-none mb-6"
              style={{ letterSpacing: '0.15em' }}
            >
              Hinton
              <br />
              <span className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white/70" style={{ letterSpacing: '0.2em' }}>
                Enterprises
              </span>
            </h1>

            <div className="w-24 h-px bg-[#D4AF37] mx-auto my-8"></div>

            <p
              data-testid="hero-tagline"
              className="text-sm md:text-base text-white/50 font-light tracking-[0.15em] uppercase"
            >
              Connecting People. Brands. Opportunities.
            </p>
          </motion.div>
        </div>

        <motion.div
          data-testid="scroll-indicator"
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
          initial={SCROLL_HIDDEN}
          animate={scrollAnimate}
          transition={SCROLL_TRANSITION}
        >
          <ChevronDown size={20} strokeWidth={1} className="text-[#D4AF37]/60 animate-bounce" />
        </motion.div>
      </section>
    </div>
  );
};

export default OnePageHome;
